import { Add } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Fab, Dialog, DialogContent, Box, Typography, TextField, DialogActions, Button } from '@mui/material'
import { FC, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { fullName } from '../../../../domain/Account/AccountDisplay'
import { getColorForContactFollowUpType, getReadableContactFollowUpType } from '../../../../domain/Animal/AnimalDisplay'
import {
  AccountDtoRoles,
  AnimalDto,
  ContactHostedFamilyFollowUpDto,
  ContactHostedFamilyFollowUpDtoType,
} from '../../../../interactors/gen/backendClient'
import { useAccountsStore } from '../../../../store/AccountsStore'
import { useEditAnimalMutation } from '../../../../store/useEditAnimalMutation'
import { toDateOnly } from '../../../../utils/date/DateOnly'
import { isValidPastDateCoherence } from '../../../../utils/date/isValidPastDateCoherence'
import { useDialogOpened } from '../../../../utils/hooks/useDialogOpened'
import { ColoredChipSelectField } from '../../../common/ColoredChipSelectField'
import { ControlledAutoCompleteWithCustomValue } from '../../../common/ControlledAutocompleteWithCustomValue'
import { ControlledDateField } from '../../../common/ControlledDateField'
import { spacingItem } from '../../../theme'
import { addPrimaryCtaSx, addSecondaryFabSx } from '../../../common/addPrimaryCtaSx'
import { useNavigate } from 'react-router-dom'
import { PATHS } from '../../../PATHS'
import dayjs from 'dayjs'
import { animalButtonShouldBeDisabled } from '../../../../utils/animalButtonShouldBeDisabled'
import { useCanEditAnimal } from '../../../../store/useCanEditAnimal'

type FormParams = {
  type: ContactHostedFamilyFollowUpDtoType
  date: string
  hostFamilyAccountId: string
  interlocutorAccountId: string
  description?: string
}

interface Props {
  animal: AnimalDto
  contactFollowUps: Array<ContactHostedFamilyFollowUpDto>
  setContactFollowUps: (contactFollowUps: Array<ContactHostedFamilyFollowUpDto>) => void
  disabled?: boolean
}

export const NewContactFollowUpModal: FC<Props> = ({
  animal,
  contactFollowUps,
  setContactFollowUps,
  disabled = false,
}) => {
  const queryParams = new URLSearchParams(location.search)
  const { isDialogOpen, openDialog, closeDialog } = useDialogOpened()
  const navigate = useNavigate()
  const accountsStore = useAccountsStore()
  const canEditAnimal = useCanEditAnimal()
  const editAnimalMutation = useEditAnimalMutation({})

  const allHostFamilies = accountsStore.members.filter((account) => account.roles.includes(AccountDtoRoles.HostFamily))

  const allMembers = accountsStore.members.filter((account) => !account.roles.includes(AccountDtoRoles.Veterinary))

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormParams>({
    defaultValues: {
      type: undefined,
      date: '',
      description: '',
      hostFamilyAccountId:
        queryParams.get('hostFamilyInCharge') || animal.responsibilities?.hostFamilyInChargeAccountId,
      interlocutorAccountId: queryParams.get('memberInCharge') || '',
    },
    mode: 'onChange',
  })

  const onSubmit = (data: FormParams) => {
    const newFollowUp = {
      ...data,
      date: toDateOnly(data.date),
      created: {
        by: accountsStore.connectedAccount?.id || '',
        at: dayjs().toISOString(),
      },
    }
    setContactFollowUps([...contactFollowUps, newFollowUp as ContactHostedFamilyFollowUpDto])
    closeDialog()
    reset()
  }

  useEffect(() => {
    queryParams.get('card') === 'newContactFollowUp' && openDialog()
  }, [queryParams.get('card')])

  const hostFamilyAccountId = watch('hostFamilyAccountId')
  useEffect(() => {
    if (hostFamilyAccountId === 'addAccount') {
      reset({ hostFamilyAccountId: undefined })
      navigate(
        `${PATHS.ajouterBenevole.absolute}?from=animaux/${animal.id}?tab=familles&role=${AccountDtoRoles.HostFamily}&card=newContactFollowUp`
      )
    }
  }, [hostFamilyAccountId])

  const interlocutorAccountId = watch('interlocutorAccountId')
  useEffect(() => {
    if (interlocutorAccountId === 'addAccount') {
      reset({ interlocutorAccountId: undefined })
      navigate(
        `${PATHS.ajouterBenevole.absolute}?from=animaux/${animal.id}?tab=familles&role=${AccountDtoRoles.Member}&card=newContactFollowUp`
      )
    }
  }, [interlocutorAccountId])

  if (!canEditAnimal) {
    return null
  }

  return (
    <>
      <Fab
        color="primary"
        variant="extended"
        onClick={openDialog}
        disabled={animalButtonShouldBeDisabled(animal) || disabled}
        sx={addSecondaryFabSx}
      >
        <Add sx={{ mr: 1 }} />
        Ajouter un suivi
      </Fab>
      <Dialog
        open={isDialogOpen}
        onClose={closeDialog}
        PaperProps={{
          style: { borderRadius: 15, padding: 15 },
        }}
      >
        <form
          onSubmit={handleSubmit(onSubmit)}
          onKeyDown={(e: React.KeyboardEvent<HTMLFormElement>) => {
            if (e.key === 'Enter') {
              e.preventDefault()
            }
          }}
        >
          <DialogContent>
            <Box>
              <Typography variant="h4" component="h2" marginBottom={4}>
                Ajouter un suivi
              </Typography>
              <ColoredChipSelectField
                sx={{ ...spacingItem, width: '100%' }}
                control={control}
                error={errors.type}
                fieldName="type"
                size="small"
                label="Type de suivi"
                options={Object.values(ContactHostedFamilyFollowUpDtoType).map(
                  (status: ContactHostedFamilyFollowUpDtoType) => ({
                    label: getReadableContactFollowUpType(status),
                    value: status,
                    color: getColorForContactFollowUpType(status),
                  })
                )}
                requiredRule={'Le type de suivi est requis'}
              />

              <ControlledDateField
                control={control}
                validate={(value) => isValidPastDateCoherence(value) || value === null}
                error={errors.date}
                fieldName={'date'}
                label="Date"
                requiredRule={'La date est requise'}
                size="small"
                sx={spacingItem}
              />

              <ControlledAutoCompleteWithCustomValue
                control={control}
                fieldName={'hostFamilyAccountId'}
                size="small"
                options={[
                  { value: 'addAccount', label: "Ajouter une famille d'accueil" },
                  ...allHostFamilies
                    .sort((a, b) => (a.firstName + a.lastName).localeCompare(b.firstName + b.lastName))
                    .map((option) => ({ label: fullName(option), value: option.id })),
                ]}
                label="Famille d'accueil"
                error={errors.hostFamilyAccountId}
                requiredRule={"La famille d'accueil est requise"}
                defaultValue={''}
                sx={spacingItem}
                highlight
              />

              <ControlledAutoCompleteWithCustomValue
                control={control}
                fieldName={'interlocutorAccountId'}
                size="small"
                options={[
                  { value: 'addAccount', label: 'Ajouter un membre' },
                  ...allMembers
                    .sort((a, b) => (a.firstName + a.lastName).localeCompare(b.firstName + b.lastName))
                    .map((option) => ({ label: fullName(option), value: option.id })),
                ]}
                label="Interlocuteur"
                error={errors.interlocutorAccountId}
                requiredRule={"L'interlocuteur est requis"}
                defaultValue={''}
                sx={spacingItem}
                highlight
              />

              <TextField
                id="description-input"
                label="Détails"
                type="text"
                fullWidth
                {...register('description')}
                sx={spacingItem}
                size="small"
                multiline
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button data-testid="cancel" onClick={closeDialog}>
              Annuler
            </Button>
            <LoadingButton
              data-testid="apply-changes"
              variant="contained"
              type="submit"
              loading={editAnimalMutation.isLoading}
              sx={addPrimaryCtaSx}
            >
              Valider
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>
    </>
  )
}
